import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Mail } from 'lucide-react';
import { newsletterApi, config, ApiError } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const LandingPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await newsletterApi.subscribe(email);
      setPopupMessage(response.message || 'Email successfully submitted, Thanks!');
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setEmail('');
      }, 3000);
    } catch (error) {
      if (error instanceof ApiError) {
        setPopupMessage(error.message || 'There was a problem. Please try again.');
      } else {
        setPopupMessage('There was a problem submitting your email. Please try again later.');
      }
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-white flex">
      {/* Contributor button — absolute top-right of the screen */}
      <Link
        to="/contributor"
        className="absolute top-6 right-6 z-10 inline-flex items-center rounded-full bg-black px-5 py-2.5 text-sm font-medium text-white hover:bg-zinc-800 transition-colors"
      >
        I am a contributor
      </Link>

      {/* Left Section */}
      <div className="w-full lg:w-1/2 flex flex-col min-h-screen">
        {/* Main Content - Centered */}
        <div className="flex-1 flex items-center justify-center px-8">
          <div className="w-full max-w-xl">
            {/* Header - Logo + Title */}
            <div className="flex items-center justify-center gap-4 mb-12">
              <img
                src="/images/CruxLabs_icon.png"
                alt="CruxLabs"
                className="w-14 h-14"
              />
              <span className="text-5xl lg:text-6xl font-bold text-black tracking-tight">
                Crux Labs
              </span>
            </div>

            <h2 className="text-2xl font-bold text-black mb-6">
              Coming Soon
            </h2>

            <p className="text-gray-600 text-lg mb-10 leading-relaxed">
              Our app is live, and our website is currently under construction.
              Thanks for being part of the early journey.
            </p>

            {/* Newsletter Form */}
            <div>
              <form onSubmit={handleSubmit} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isSubmitting}
                  className="flex-1 h-12 rounded-full px-5 border-gray-300"
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-12 rounded-full px-8 bg-pink-500 hover:bg-pink-600 font-semibold"
                >
                  {isSubmitting ? '...' : 'GET NOTIFIED'}
                </Button>
              </form>
              <p className="text-gray-400 text-sm mt-3">
                Sign up to get the latest updates
              </p>
            </div>

            {/* App Store Links */}
            <div className="flex justify-center gap-4 mt-8">
              <a
                href={config.googlePlayUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105"
              >
                <img
                  src="/images/Google-Play.png"
                  alt="Get it on Google Play"
                  className="h-11"
                />
              </a>
              <a
                href={config.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105"
              >
                <img
                  src="/images/App-Store.png"
                  alt="Download on App Store"
                  className="h-11"
                />
              </a>
            </div>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <footer className="px-8 py-6">
          {/* Social + Legal Links */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="text-black font-medium text-sm">Stay in touch:</span>
              <div className="flex gap-3">
                <a
                  href="https://www.instagram.com/cruxlabs.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-pink-100 hover:text-pink-500 transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61578944374744"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-blue-100 hover:text-blue-600 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href={`mailto:${config.contactEmail}`}
                  className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 hover:text-gray-900 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div className="flex gap-6">
              <Link to="/privacy-policy" className="text-sm text-gray-400 hover:text-gray-600">
                Privacy Policy
              </Link>
              <Link to="/terms-of-use" className="text-sm text-gray-400 hover:text-gray-600">
                Terms of Use
              </Link>
            </div>
          </div>
        </footer>
      </div>

      {/* Right Section - Background Image */}
      <div
        className="hidden lg:block w-1/2 bg-contain bg-no-repeat bg-center"
        style={{ backgroundImage: 'url(/images/Wire_1.png)' }}
      />

      {/* Popup */}
      {showPopup && (
        <div className="fixed top-1/3 left-1/2 -translate-x-1/2 bg-white px-8 py-5 rounded-2xl shadow-2xl z-50 text-lg border border-gray-100">
          {popupMessage}
        </div>
      )}
    </div>
  );
};

export default LandingPage;
